import {
  faCalendarDay,
  faCheckDouble,
  faCopyright,
  faDownload,
  faShop,
} from '@fortawesome/pro-regular-svg-icons'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import IWBadgeGroup from 'shared/components/thunderbolt/IWBadgeGroup'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWCardBadge from 'shared/components/thunderbolt/IWCardBadge'
import IWGenericCard, {
  IWGenericCardBody,
  IWGenericCardFooter,
  IWGenericCardHeader,
} from 'shared/components/thunderbolt/IWGenericCard'
import styled from 'styled-components'
import { SubmissionHistory } from '../types'

const StyledCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 0.5rem;
`

const StyledIWGenericCardHeader = styled(IWGenericCardHeader)`
  display: flex;
  align-items: center;
`

const StyledIWGenericCardInfo = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.25rem;
  flex: 1;
`

const StyledGenericCardBody = styled(IWGenericCardBody)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledLink = styled(Link)`
  color: ${(props) => props.theme.palette.primary[700]};
  font-size: ${(props) => props.theme.typography.fontSizes.base};
  font-weight: ${(props) => props.theme.typography.fontWeights.medium};
  text-decoration: none;

  align-self: center;

  &:hover {
    text-decoration: underline;
  }
`

const StyledIWGenericCardFooter = styled(IWGenericCardFooter)`
  padding: 1rem 1.5rem;
  background: ${(props) => props.theme.palette.grey[100]};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

export type LsPreviousSubmitted = {
  market: SubmissionHistory['market']
  brand: SubmissionHistory['brand']
  timezone: string
  createdAt: SubmissionHistory['createdAt']
  submissionGroupId: SubmissionHistory['submissionGroupId']
  data: SubmissionHistory[]
}

type Props = {
  items: LsPreviousSubmitted[]
  onDownload: (data: SubmissionHistory[]) => void
  onSelect: (data: SubmissionHistory[]) => void
}

const Card = ({
  market,
  brand,
  createdAt,
  timezone,
  data,
  isLoading = false,
  isSelected = false,
  onDownload,
  onSelect,
}: LsPreviousSubmitted & {
  isSelected?: boolean
  isLoading?: boolean
  onDownload?: (data: SubmissionHistory[]) => void
  onSelect?: (data: SubmissionHistory[]) => void
}) => {
  const { t } = useTranslation()

  const renderFooterButtons = () => {
    return (
      <div style={{ display: 'flex' }}>
        <IWButton
          style={{ marginRight: '0.5rem' }}
          variant="outline"
          color="primary"
          data-testid="ls-pod-actions-download"
          icon={faDownload}
          disabled={isLoading}
          iconPosition="leading"
          onClick={() => onDownload && onDownload(data)}
          hasSolidBackground
        >
          {t('button.download')}
        </IWButton>

        {isSelected && (
          <IWButton
            style={{ marginRight: '0.5rem' }}
            variant="alternative"
            color="primary"
            data-testid="ls-pod-actions-select"
            icon={faCheckDouble}
            disabled={isLoading}
            iconPosition="leading"
            onClick={() => onSelect && onSelect(data)}
          >
            {t('button.select')}
          </IWButton>
        )}

        {!isSelected && (
          <IWButton
            style={{ marginRight: '0.5rem' }}
            variant="outline"
            color="primary"
            data-testid="ls-pod-actions-select"
            icon={faCheckDouble}
            disabled={isLoading}
            iconPosition="leading"
            onClick={() => onSelect && onSelect(data)}
            hasSolidBackground
          >
            {t('button.select')}
          </IWButton>
        )}
      </div>
    )
  }

  return (
    <IWGenericCard>
      <StyledIWGenericCardHeader>
        <StyledIWGenericCardInfo>
          <div style={{ display: 'flex' }}>
            <StyledLink to={`/tools/load-scheduling/submission-history`}>
              {market} - {brand}
            </StyledLink>
          </div>
        </StyledIWGenericCardInfo>
      </StyledIWGenericCardHeader>

      <StyledGenericCardBody>
        <IWBadgeGroup>
          <IWCardBadge label={market.toLocaleUpperCase()} icon={faShop} />
          <IWCardBadge label={brand.toLocaleUpperCase()} icon={faCopyright} />
        </IWBadgeGroup>
        <IWBadgeGroup>
          <IWCardBadge
            label={t('loadScheduling.labels.submittedOn', {
              value: DateTime.fromISO(createdAt as string, {
                zone: timezone,
              }).toLocaleString(DateTime.DATETIME_FULL),
            })}
            icon={faCalendarDay}
          />
        </IWBadgeGroup>
      </StyledGenericCardBody>

      <StyledIWGenericCardFooter>
        {renderFooterButtons()}
      </StyledIWGenericCardFooter>
    </IWGenericCard>
  )
}

export const LSPreviousSubmittedTab = ({
  items,
  onDownload,
  onSelect,
}: Props) => {
  const ordered = orderBy(items, (item) => item.createdAt, 'desc')

  return (
    <StyledCardList>
      {ordered.map((item) => (
        <Card
          market={item.market}
          brand={item.brand}
          timezone={item.timezone}
          createdAt={item.createdAt}
          submissionGroupId={item.submissionGroupId}
          data={item.data}
          onSelect={onSelect}
          onDownload={onDownload}
        />
      ))}
    </StyledCardList>
  )
}
